
import { useState, useEffect } from 'react';
import Button from '../../components/base/Button';
import SEO from '../../components/SEO';

const EventsPage = () => {
  const [activeForm, setActiveForm] = useState<string | null>(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    experience: '',
    interests: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  const questions = [
    "What if knowledge could transform entire communities?",
    "Why should geography limit your potential?",
    "How do we build the next generation of innovators?",
    "What if every conversation could spark a revolution?"
  ];

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuestion((prev) => (prev + 1) % questions.length);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent, eventType: string) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    const submitUrls = {
      'tech-fest': 'https://readdy.ai/api/form/d44ukets1qnclj70j4bg',
      'ai-masterclass': 'https://readdy.ai/api/form/d44ukets1qnclj70j4ag',
      'women-tech': 'https://readdy.ai/api/form/d44ukets1qnclj70j4b0'
    };

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });
      formDataToSend.append('event_type', eventType);

      const response = await fetch(submitUrls[eventType as keyof typeof submitUrls], {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          organization: '',
          experience: '',
          interests: ''
        });
        setTimeout(() => {
          setActiveForm(null);
          setSubmitStatus(null);
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const RegistrationForm = ({ eventName, eventType }: { eventName: string; eventType: string }) => (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl max-w-md w-full max-h-[90vh] overflow-y-auto border border-[#6D03F4]/30">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-white">Join {eventName}</h3>
            <button
              onClick={() => setActiveForm(null)}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>

          {submitStatus === 'success' && (
            <div className="mb-4 p-4 bg-gradient-to-r from-green-500/20 to-emerald-600/20 border border-green-500/30 text-green-400 rounded-lg">
              Registration successful! We'll contact you soon with revolutionary details.
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-4 p-4 bg-gradient-to-r from-red-500/20 to-orange-600/20 border border-red-500/30 text-red-400 rounded-lg">
              Registration failed. Please try again.
            </div>
          )}

          <form onSubmit={(e) => handleSubmit(e, eventType)} data-readdy-form id={`${eventType}-registration`}>
            <div className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D03F4] text-white placeholder-gray-400"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D03F4] text-white placeholder-gray-400"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D03F4] text-white placeholder-gray-400"
                />
              </div>

              <div>
                <label htmlFor="organization" className="block text-sm font-medium text-gray-300 mb-2">
                  Organization/Institution
                </label>
                <input
                  type="text"
                  id="organization"
                  name="organization"
                  value={formData.organization}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D03F4] text-white placeholder-gray-400"
                />
              </div>

              <div>
                <label htmlFor="experience" className="block text-sm font-medium text-gray-300 mb-2">
                  Experience Level
                </label>
                <select
                  id="experience"
                  name="experience"
                  value={formData.experience}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 pr-8 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D03F4] text-white"
                >
                  <option value="" className="bg-gray-900">Select your level</option>
                  <option value="beginner" className="bg-gray-900">Beginner</option>
                  <option value="intermediate" className="bg-gray-900">Intermediate</option>
                  <option value="advanced" className="bg-gray-900">Advanced</option>
                  <option value="expert" className="bg-gray-900">Expert</option>
                </select>
              </div>

              <div>
                <label htmlFor="interests" className="block text-sm font-medium text-gray-300 mb-2">
                  Areas of Interest
                </label>
                <textarea
                  id="interests"
                  name="interests"
                  value={formData.interests}
                  onChange={handleInputChange}
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D03F4] text-white placeholder-gray-400 resize-none"
                  placeholder="Tell us about your interests and what you hope to learn..."
                />
                <div className="text-xs text-gray-400 mt-1">
                  {formData.interests.length}/500 characters
                </div>
              </div>
            </div>

            <div className="mt-8 flex gap-4">
              <button
                type="button"
                onClick={() => setActiveForm(null)}
                className="flex-1 px-6 py-3 border border-white/30 text-gray-300 rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-gradient-to-r from-[#6D03F4] to-[#007FFF] text-white px-6 py-3 rounded-lg hover:from-[#5A02D1] hover:to-[#0066CC] transition-colors disabled:opacity-50 whitespace-nowrap"
              >
                {isSubmitting ? 'Joining...' : 'Join Revolution'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  const events = [
    {
      title: "TheSpire Tech Fest 1.0 — Beyond the Buzz",
      date: "April 2026",
      location: "Abuja Tech Hub",
      description: "A groundbreaking tech festival bringing together innovators, creators, and industry revolutionaries.",
      image: "https://readdy.ai/api/search-image?query=African%20tech%20conference%20with%20diverse%20speakers%20on%20stage%2C%20purple%20and%20blue%20lighting%2C%20modern%20conference%20hall%2C%20engaged%20Black%20audience%2C%20professional%20tech%20event%20photography%2C%20innovative%20atmosphere&width=600&height=400&seq=14&orientation=landscape",
      type: "Revolutionary Conference",
      status: "upcoming",
      intensity: "Game-Changing"
    },
    {
      title: "AI & Machine Learning Masterclass",
      date: "June 2026",
      location: "Virtual Event",
      description: "Deep dive into AI applications and machine learning fundamentals with industry revolutionaries.",
      image: "https://readdy.ai/api/search-image?query=African%20AI%20expert%20presenting%20machine%20learning%20concepts%20on%20large%20screen%2C%20futuristic%20tech%20presentation%2C%20purple%20ambient%20lighting%2C%20engaged%20audience%2C%20professional%20educational%20setting&width=600&height=400&seq=15&orientation=landscape",
      type: "Mind-Bending Masterclass",
      status: "upcoming",
      intensity: "Revolutionary"
    },
    {
      title: "Women in Tech Leadership Summit",
      date: "February 2026",
      location: "Lagos Innovation Center",
      description: "Empowering women leaders in technology across Africa to dominate the future.",
      image: "https://readdy.ai/api/search-image?query=African%20women%20tech%20leaders%20panel%20discussion%2C%20professional%20conference%20setting%2C%20diverse%20Black%20women%20speakers%2C%20modern%20stage%20design%2C%20inspiring%20leadership%20event%20photography&width=600&height=400&seq=16&orientation=landscape",
      type: "Empowerment Summit",
      status: "upcoming",
      intensity: "Life-Changing"
    }
  ];

  const scholarships = [
    {
      title: "Future Innovators Revolution",
      amount: "₦500,000",
      description: "Full scholarship for outstanding students pursuing tech and creative careers to dominate the future.",
      deadline: "April 2026",
      recipients: 25,
      intensity: "Life-Changing",
      criteria: ["Academic excellence", "Financial need", "Innovation potential", "Community impact"]
    },
    {
      title: "Women in Tech Domination",
      amount: "₦300,000",
      description: "Supporting women entering technology fields with comprehensive training to revolutionize the industry.",
      deadline: "February 2026",
      recipients: 15,
      intensity: "Game-Changing",
      criteria: ["Female applicant", "Tech course enrollment", "Leadership potential", "Career commitment"]
    },
    {
      title: "Rural Development Revolution",
      amount: "₦400,000",
      description: "Empowering students from rural communities to access quality tech education and transform their regions.",
      deadline: "June 2026",
      recipients: 20,
      intensity: "Revolutionary",
      criteria: ["Rural background", "Community service", "Academic merit", "Technology interest"]
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "EventSeries",
    "name": "TheSpire Revolutionary Events & Impact",
    "description": "Building communities, sharing knowledge, and creating opportunities through revolutionary events, scholarships, and transformative experiences.",
    "url": `${import.meta.env.VITE_SITE_URL || "https://thespire.com.ng"}/events`,
    "organizer": {
      "@type": "Organization",
      "name": "TheSpire",
      "url": `${import.meta.env.VITE_SITE_URL || "https://thespire.com.ng"}`,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Abuja",
        "addressCountry": "Nigeria"
      }
    },
    "event": events.map(event => ({
      "@type": "Event",
      "name": event.title,
      "description": event.description,
      "startDate": event.date,
      "location": {
        "@type": "Place",
        "name": event.location,
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "Nigeria"
        }
      },
      "eventStatus": event.status === 'upcoming' ? "EventScheduled" : "EventPostponed",
      "organizer": {
        "@type": "Organization",
        "name": "TheSpire"
      }
    })),
    "offers": scholarships.map(scholarship => ({
      "@type": "Offer",
      "name": scholarship.title,
      "description": scholarship.description,
      "price": scholarship.amount.replace('₦', ''),
      "priceCurrency": "NGN",
      "validThrough": scholarship.deadline
    }))
  };

  return (
    <>
      <SEO 
        title="Revolutionary Events & Conferences - TheSpire"
        description="Join TheSpire's revolutionary tech events, conferences, and workshops. Connect with industry leaders and expand your knowledge in AI, machine learning, and technology."
        keywords="revolutionary tech events, conferences, AI workshops, machine learning, women in tech, TheSpire events, technology revolution"
        type="website"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen bg-[#0A0A0A] text-white overflow-hidden">
        {/* Dynamic Background */}
        <div 
          className="fixed inset-0 opacity-30 pointer-events-none"
          style={{
            background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(109, 3, 244, 0.3) 0%, rgba(0, 127, 255, 0.2) 25%, transparent 50%)`
          }}
        />
        
        {/* Floating Particles */}
        <div className="fixed inset-0 pointer-events-none">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-[#6D03F4] rounded-full animate-pulse"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${2 + Math.random() * 3}s`
              }}
            />
          ))}
        </div>

        {/* Hero Section */}
        <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
          <div className="absolute inset-0 bg-gradient-to-br from-[#6D03F4]/20 via-transparent to-[#007FFF]/20" />
          
          <div className="relative z-10 max-w-7xl mx-auto text-center">
            {/* Rotating Questions */}
            <div className="mb-8 h-16 flex items-center justify-center">
              <p className="text-lg md:text-xl text-[#6D03F4] font-medium transition-all duration-1000 transform">
                {questions[currentQuestion]}
              </p>
            </div>

            <h1 className="text-6xl md:text-8xl lg:text-9xl font-black mb-8 leading-none">
              <span className="block text-white">REVOLUTIONARY</span>
              <span className="block bg-gradient-to-r from-[#6D03F4] to-[#007FFF] bg-clip-text text-transparent">
                EVENTS
              </span>
            </h1>
            
            <p className="text-xl md:text-3xl text-gray-300 mb-12 max-w-4xl mx-auto font-light">
              Join us for <span className="text-[#6D03F4] font-bold">cutting-edge tech events</span>, workshops, and networking opportunities that 
              <span className="text-[#007FFF] font-bold"> shape the future</span> of technology
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Button size="lg" className="bg-gradient-to-r from-[#6D03F4] to-[#007FFF] hover:from-[#5A02D1] hover:to-[#0066CC] text-white px-12 py-4 text-lg font-bold transform hover:scale-105 transition-all duration-300 whitespace-nowrap">
                JOIN THE REVOLUTION
              </Button>
              <Button 
                variant="outline" 
                size="lg" 
                className="border-2 border-[#6D03F4] text-[#6D03F4] hover:bg-[#6D03F4] hover:text-white px-12 py-4 text-lg font-bold transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
              >
                EXPLORE EVENTS
              </Button>
            </div>
          </div>

          {/* Animated Grid */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `linear-gradient(rgba(109, 3, 244, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(109, 3, 244, 0.3) 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
              animation: 'grid-move 20s linear infinite'
            }} />
          </div>
        </section>

        {/* Revolutionary Events */}
        <section className="py-20 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-5xl md:text-7xl font-black text-white mb-6">
                UPCOMING
                <span className="block bg-gradient-to-r from-[#6D03F4] to-[#007FFF] bg-clip-text text-transparent">
                  REVOLUTIONS
                </span>
              </h2>
              <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
                Mark your calendars for these <span className="text-[#6D03F4] font-bold">incredible opportunities</span> to learn, network, and grow
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {events.map((event, index) => (
                <div key={index} className="group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl overflow-hidden border border-white/20 hover:border-[#6D03F4]/50 transition-all duration-500 hover:transform hover:scale-105">
                  <div className="relative h-48">
                    <img
                      src={event.image}
                      alt={event.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    <div className="absolute top-4 left-4">
                      <span className="bg-gradient-to-r from-[#6D03F4] to-[#007FFF] text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                        {event.intensity}
                      </span>
                    </div>
                    <div className="absolute top-4 right-4 text-white text-sm font-semibold">
                      {event.date}
                    </div>
                    <div className="absolute bottom-4 left-4">
                      <span className="bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full text-xs font-semibold">
                        {event.type}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-white mb-3">{event.title}</h3>
                    <p className="text-gray-300 mb-4">{event.description}</p>
                    
                    <div className="space-y-2 mb-6">
                      <div className="flex items-center text-sm text-gray-400">
                        <i className="ri-map-pin-line mr-2 text-[#6D03F4]"></i>
                        {event.location}
                      </div>
                    </div>
                    
                    <a
                      href="https://chat.whatsapp.com/F82KXNs9f69C1zBJ6eoJrB"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-gradient-to-r from-red-500 to-[#6D03F4] hover:from-red-600 hover:to-[#5A02D1] text-white px-6 py-3 rounded-lg font-bold transform hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer inline-block text-center"
                    >
                      JOIN REVOLUTION
                    </a>
                  </div>
                  
                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-[#6D03F4]/20 to-[#007FFF]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Revolutionary Scholarships */}
        <section className="py-20 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-5xl md:text-7xl font-black text-white mb-6">
                SCHOLARSHIPS &
                <span className="block bg-gradient-to-r from-[#6D03F4] to-[#007FFF] bg-clip-text text-transparent">
                  OPPORTUNITIES
                </span>
              </h2>
              <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
                Supporting the next generation of <span className="text-[#6D03F4] font-bold">tech revolutionaries</span> through educational scholarships
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {scholarships.map((scholarship, index) => (
                <div key={index} className="group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-8 border border-white/20 hover:border-[#6D03F4]/50 transition-all duration-500 hover:transform hover:scale-105">
                  <div className="mb-4">
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-gradient-to-r from-[#6D03F4] to-[#007FFF] text-white">
                      {scholarship.intensity}
                    </span>
                  </div>
                  
                  <div className="w-12 h-12 bg-gradient-to-r from-[#6D03F4] to-[#007FFF] rounded-lg flex items-center justify-center mb-6">
                    <i className="ri-lightbulb-line text-white text-xl"></i>
                  </div>
                  
                  <h3 className="text-xl font-bold text-white mb-3">{scholarship.title}</h3>
                  <p className="text-gray-300 mb-6">{scholarship.description}</p>
                  
                  <div className="space-y-3 mb-6">
                    <div className="flex items-center text-sm text-gray-400">
                      <i className="ri-money-dollar-circle-line mr-2 text-[#6D03F4]"></i>
                      Up to {scholarship.amount}
                    </div>
                    <div className="flex items-center text-sm text-gray-400">
                      <i className="ri-calendar-line mr-2 text-[#6D03F4]"></i>
                      Deadline: {scholarship.deadline}
                    </div>
                    <div className="flex items-center text-sm text-gray-400">
                      <i className="ri-user-line mr-2 text-[#6D03F4]"></i>
                      {scholarship.recipients} Recipients
                    </div>
                  </div>
                  
                  <button className="w-full bg-gradient-to-r from-[#6D03F4] to-[#007FFF] text-white py-3 px-6 rounded-lg hover:from-[#5A02D1] hover:to-[#0066CC] transition-all duration-300 font-bold transform hover:scale-105 whitespace-nowrap">
                    APPLY NOW
                  </button>
                  
                  {/* Hover Effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-[#6D03F4]/20 to-[#007FFF]/20 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Revolutionary CTA */}
        <section className="py-20 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-[#6D03F4] to-[#007FFF] opacity-20" />
          <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-5xl md:text-7xl font-black text-white mb-8">
              READY TO JOIN
              <span className="block bg-gradient-to-r from-[#6D03F4] to-[#007FFF] bg-clip-text text-transparent">
                OUR REVOLUTION?
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-300 mb-12">
              Don't miss out on these <span className="text-[#6D03F4] font-bold">incredible opportunities</span> to learn, grow, and connect with revolutionary minds.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button className="bg-white text-[#6D03F4] px-12 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors transform hover:scale-105 duration-300 whitespace-nowrap">
                VIEW ALL EVENTS
              </button>
              <button className="border-2 border-white text-white px-12 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-[#6D03F4] transition-colors transform hover:scale-105 duration-300 whitespace-nowrap">
                CONTACT REVOLUTION
              </button>
            </div>
          </div>
        </section>
      </div>

      {/* Registration Forms */}
      {activeForm === 'tech-fest' && (
        <RegistrationForm eventName="TheSpire Tech Fest 1.0" eventType="tech-fest" />
      )}
      {activeForm === 'ai-masterclass' && (
        <RegistrationForm eventName="AI & Machine Learning Masterclass" eventType="ai-masterclass" />
      )}
      {activeForm === 'women-tech' && (
        <RegistrationForm eventName="Women in Tech Leadership Summit" eventType="women-tech" />
      )}

      <style jsx>{`
        @keyframes grid-move {
          0% { transform: translate(0, 0); }
          100% { transform: translate(50px, 50px); }
        }
      `}</style>
    </>
  );
};

export default EventsPage;
